import { Inject, Injectable } from '@angular/core';
import {
  ApiService,
  AuthService,
  EnvironmentService,
  HttpRequestService,
  LogService,
  NotificationService,
  PagedSearch,
  UtilService,
  PagedResultGeneric,
} from 'src/app/shared';
import { ServiceApiService } from 'src/app/shared/api/service-api.service';
import { EcomCategoryDto } from './dto/ecom-category.dto';
import { EcomItemCopyDto } from './dto/ecom-item-copy.dto';
import { EcomItemEditDto } from './dto/ecom-item-edit.dto';
import { EcomItemImageDto } from './dto/ecom-item-image.dto';
import { EcomItemTypeDto } from './dto/ecom-item-type.dto';
import { EcomItemDto } from './dto/ecom-item.dto';
import { EcomSearch } from './dto/ecom-search.dto';
import { EcomShippingItemTypeDto } from './dto/ecom-shipping-item-type.dto';
import { EcomShippingTypeDto } from './dto/ecom-shipping-type.dto';

@Injectable({
  providedIn: 'root',
})
export class EcomService extends ServiceApiService {
  constructor(
    @Inject(AuthService) authService: AuthService,
    @Inject(UtilService) utilService: UtilService,
    @Inject(LogService) logService: LogService,
    @Inject(HttpRequestService) httpRequestService: HttpRequestService,
    @Inject(EnvironmentService) environmentService: EnvironmentService,
    @Inject(NotificationService) notificationService: NotificationService
  ) {
    super(authService, utilService, logService, httpRequestService, environmentService, notificationService, 'eCommerce');
  }

  public GetItem(id: number): Promise<EcomItemDto> {
    let url = `ecommerce/v2/item/${id}`;
    let options = this.getDefaultApiCacheOptionsForNoCache();
    return this.get(url, options);
  }

  async SaveItem(eCommerceItem: EcomItemDto): Promise<number> {
    let url = `ecommerce/v2/item`;
    if (eCommerceItem.ItemId) {
      await this.put(url, eCommerceItem);
      return eCommerceItem.ItemId;
    } else {
      return await this.post(url, eCommerceItem);
    }
  }

  public GetItemTypes(): Promise<PagedResultGeneric<EcomItemTypeDto>> {
    let url = `ecommerce/v2/itemtypes`;
    return this.post(url, { PageNumber: 0, PageSize: 0 } as PagedSearch);
  }

  public search(searchParams: EcomSearch): Promise<PagedResultGeneric<EcomItemDto>> {
    let url = `ecommerce/v2/items`;
    return this.post(url, searchParams);
  }

  public GetCategories(): Promise<PagedResultGeneric<EcomCategoryDto>> {
    let url = `ecommerce/v2/categories`;
    return this.post(url, { PageNumber: 0, PageSize: 0 } as PagedSearch);
  }

  public GetShippingTypes(): Promise<PagedResultGeneric<EcomShippingTypeDto>> {
    let url = `ecommerce/v2/shippingtypes`;
    return this.post(url, { PageNumber: 0, PageSize: 0 } as PagedSearch);
  }

  public GetShippingItemTypes(): Promise<PagedResultGeneric<EcomShippingItemTypeDto>> {
    let url = `ecommerce/v2/shippingitemtypes`;
    return this.post(url, { PageNumber: 0, PageSize: 0 } as PagedSearch);
  }

  public GetShippingItemType(shippingItemTypeId: number): Promise<Array<EcomShippingItemTypeDto>> {
    let url = `ecommerce/v2/shippingitemtypes/${shippingItemTypeId}`;
    return this.get(url);
  }

  public PostShippingItemType(shippingItemType: EcomShippingItemTypeDto): Promise<number> {
    let url = `ecommerce/v2/shippingitemtype`;

    if (shippingItemType.ItemShippingTypeId) {
      return this.put(url, shippingItemType);
    }

    return this.post(url, shippingItemType);
  }

  public PostItemEdit(ecommerceItemEdit: EcomItemEditDto): Promise<number> {
    let url = `ecommerce/v2/itemedit`;
    return this.post(url, ecommerceItemEdit);
  }

  public GetEcomItemImages(itemId: number, forceRefresh?: boolean): Promise<Array<EcomItemImageDto>> {
    const cacheOptions = forceRefresh ? this.getDefaultApiCacheOptionsForNoCache() : null;
    let url = `ecommerce/v2/get-s3-bucket/${itemId}`;
    return this.get(`ecommerce/v2/get-s3-bucket/${itemId}`, cacheOptions);
  }

  public InsertEcomItemImages(itemId: number, formFiles: Array<File>): Promise<void> {
    const formData = new FormData();
    for (let i = 0; i < formFiles.length; i++) {
      formData.append('files', formFiles[i], formFiles[i].name);
    }
    return this.postRaw(`ecommerce/v2/${itemId}/upload-image`, formData);
  }

  public DeleteEcomItemImage(itemId: number, fileName: string): Promise<void> {
    let url = `ecommerce/v2/delete-file?itemId=${itemId}&&fileName=${fileName}`;
    return this.delete(url);
  }

  public UpdateEcomItem(ecommerceItem: EcomItemDto): Promise<void> {
    return this.put('ecommerce/v2/item', ecommerceItem);
  }

  public DeleteEcomItem(itemId: number): Promise<void> {
    return this.delete(`ecommerce/v2/item/${itemId}`);
  }

  public copyItem(data: EcomItemCopyDto): Promise<number> {
    let url = `ecommerce/v2/itemcopy`;
    return this.post(url, data);
  }

  public UpdateEcomItemImage(ecommerceItem: EcomItemDto): Promise<void> {
    return this.put('ecommerce/v2/itemimage', ecommerceItem);
  }
}
